import { useI18n } from '../i18n'
import { BookOpen, HelpCircle, Shield, Link2, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { trackEvent } from '../lib/analytics'

interface ContentStep {
  title: string
  description: string
}

interface ContentFaq {
  question: string
  answer: string
}

interface ContentSectionData {
  guide?: {
    title: string
    description?: string
    steps: ContentStep[]
  }
  faq?: {
    title: string
    items: ContentFaq[]
  }
  features?: {
    title: string
    description?: string
    items: string[]
  }
  privacy?: {
    title: string
    description: string
  }
  related?: {
    title: string
    tools: { name: string; url: string; description: string }[]
  }
}

function GuideSection({ guide }: { guide: ContentSectionData['guide'] }) {
  if (!guide) return null
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-brand-primary" />
        {guide.title}
      </h2>
      {guide.description && (
        <p className="text-muted-foreground mb-4">{guide.description}</p>
      )}
      <ol className="space-y-4">
        {guide.steps.map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center text-sm font-medium">
              {i + 1}
            </span>
            <div>
              <h3 className="font-medium text-foreground text-sm">{step.title}</h3>
              <p className="text-sm text-muted-foreground mt-0.5">{step.description}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  )
}

function FaqItem({ item }: { item: ContentFaq }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="text-sm font-medium text-foreground flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-brand-primary shrink-0" />
          {item.question}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3 pt-0">
          <p className="text-sm text-muted-foreground leading-relaxed pl-6">{item.answer}</p>
        </div>
      )}
    </div>
  )
}

function FaqSection({ faq }: { faq: ContentSectionData['faq'] }) {
  if (!faq?.items?.length) return null
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-brand-primary" />
        {faq.title}
      </h2>
      <div className="space-y-2">
        {faq.items.map((item, i) => (
          <FaqItem key={i} item={item} />
        ))}
      </div>
    </section>
  )
}

function FeaturesSection({ features }: { features: ContentSectionData['features'] }) {
  if (!features?.items?.length) return null
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4">{features.title}</h2>
      {features.description && (
        <p className="text-muted-foreground mb-4">{features.description}</p>
      )}
      <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {features.items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/30 rounded-lg px-3 py-2.5">
            <span className="text-brand-primary mt-0.5">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function PrivacySection({ privacy }: { privacy: ContentSectionData['privacy'] }) {
  if (!privacy) return null
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-brand-primary" />
        {privacy.title}
      </h2>
      <p className="text-sm text-muted-foreground leading-relaxed">{privacy.description}</p>
    </section>
  )
}

function RelatedToolsSection({ related }: { related: ContentSectionData['related'] }) {
  if (!related?.tools?.length) return null
  return (
    <section className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
        <Link2 className="w-5 h-5 text-brand-primary" />
        {related.title}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {related.tools.map((tool, i) => (
          <a
            key={i}
            href={tool.url}
            className="block p-3 rounded-lg border border-border hover:border-brand-primary/30 hover:bg-muted/30 transition-colors"
            onClick={() => trackEvent('related_tool_click', { target_tool: tool.name, target_url: tool.url })}
          >
            <h3 className="text-sm font-medium text-foreground">{tool.name}</h3>
            <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
          </a>
        ))}
      </div>
    </section>
  )
}

export function ContentSection() {
  const { translations } = useI18n()
  const content = (translations as Record<string, unknown>).content as ContentSectionData | undefined

  if (!content) return null

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-8 border-t border-border">
      <div className="max-w-3xl mx-auto">
        <GuideSection guide={content.guide} />
        <FeaturesSection features={content.features} />
        <PrivacySection privacy={content.privacy} />
        <FaqSection faq={content.faq} />
        <RelatedToolsSection related={content.related} />
      </div>
    </div>
  )
}
